// needsCheck.ts — the content and rules for the onboarding "needs" step. Pure data and
// one pure function, so it can be tested without React. Answers are never stored: the
// step holds them in component state only, long enough to build the reveal.

export type NeedsKey = 'fear' | 'obligation' | 'guilt' | 'shame' | 'lossOfSelf';

export interface NeedsItem {
  key: NeedsKey;
  label: string;
  question: string;
  /** One short line for the reveal: what ReNurture does about this theme. */
  help: string;
}

/** Scored 0-5; a pattern is surfaced at this value or above ("Sometimes"). */
export const REVEAL_THRESHOLD = 2;

export const NEEDS_ITEMS: NeedsItem[] = [
  {
    key: 'fear',
    label: 'Fear',
    question: 'I felt like I was walking on eggshells, watching what I said or did to avoid my partner’s anger or moods.',
    help: 'Settling a body that learned to stay on alert.',
  },
  {
    key: 'obligation',
    label: 'Obligation',
    question: 'I believed my role was to keep them calm or happy, no matter what it cost me.',
    help: 'Putting down the job of keeping them calm.',
  },
  {
    key: 'guilt',
    label: 'Guilt',
    question: 'I felt responsible for their anger, sadness, or stress.',
    help: 'Letting go of responsibility for their moods.',
  },
  {
    key: 'shame',
    label: 'Shame',
    question: 'I felt bad, stupid, or ‘less than’ for letting it happen or for not leaving sooner.',
    help: 'Replacing self-blame with self-compassion.',
  },
  {
    key: 'lossOfSelf',
    label: 'Loss of self',
    question: 'I lost touch with positive feelings, such as joy, excitement, relaxation, or ambition.',
    help: 'Finding your way back to joy, and to you.',
  },
];

export const SCALE: { value: number; label: string }[] = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Very often' },
  { value: 5, label: 'Always' },
];

export type ExAnswer = 'yes' | 'no' | 'notSure';

export const EX_QUESTION = 'Does your ex still interfere with your life, now that you’ve separated?';

export const EX_OPTIONS: { value: ExAnswer; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'notSure', label: 'Not sure' },
];

export const EX_HELP = 'Stepping out of their reach and holding your boundaries.';

/** Shown above Q1-Q5 only; Q6 has its own wording. */
export const QUESTION_EYEBROW = 'In my past relationship…';

export const REVEAL_SUMMARY = 'ReNurture is designed to work on all of this. It helps you settle the trauma your body still carries and rebuild who you are.';
export const REVEAL_SUMMARY_EX = ' It also supports you to break contact with your ex and keep it on your terms.';

export const FALLBACK_REVEAL = 'You may not recognise these patterns, or not yet. Many people only see them later. ReNurture covers all five, whenever any of them shows up.';

export interface NeedsAnswers {
  scale: Partial<Record<NeedsKey, number>>;
  ex?: ExAnswer;
}

export interface RevealLine { key: NeedsKey | 'ex'; label: string; text: string }

/** The lines to show, in question order with the ex line last. Empty means "use the fallback". */
export function revealLines(answers: NeedsAnswers): RevealLine[] {
  const out: RevealLine[] = [];
  for (const it of NEEDS_ITEMS) {
    const v = answers.scale[it.key];
    if (typeof v === 'number' && v >= REVEAL_THRESHOLD) out.push({ key: it.key, label: it.label, text: it.help });
  }
  if (answers.ex === 'yes' || answers.ex === 'notSure') out.push({ key: 'ex', label: 'Your ex', text: EX_HELP });
  return out;
}

/** The summary paragraph under the rows. Empty when nothing qualifies (the fallback stands alone). */
export function revealSummary(answers: NeedsAnswers): string {
  const lines = revealLines(answers);
  if (lines.length === 0) return '';
  return lines.some((l) => l.key === 'ex') ? REVEAL_SUMMARY + REVEAL_SUMMARY_EX : REVEAL_SUMMARY;
}

export type StartKey = 'lesson' | 'settle';

/** Fear answered "Often" or above: a settled body first is the kinder lead. */
export const SETTLE_FIRST_AT = 3;

/**
 * Which of the two guided starts leads on the reveal (primary first, secondary second).
 * Decided in memory from the answers; "Look around first" is always the quiet third.
 */
export function startOrder(answers: NeedsAnswers): [StartKey, StartKey] {
  const fear = answers.scale.fear;
  return typeof fear === 'number' && fear >= SETTLE_FIRST_AT ? ['settle', 'lesson'] : ['lesson', 'settle'];
}
